<script setup lang="ts">
import { computed } from 'vue';

import type { GlobalSearchApplicationRow } from '@/core/conversations/conversation-index/conversation-index.type';
import { splitTextForHighlight } from '@/utils/text-highlight.util';
import { isPhoneLikeQuery } from '@/core/conversations/conversation-index/global-search.util';

import {
  formatCompactTimeAgo,
  getJobStateTag,
  getStageLabel,
} from '@/core/conversations/conversation-index/global-search-labels.util';

const props = defineProps<{
  row: GlobalSearchApplicationRow;
  query: string;
  /**
   * True when the row stands alone and must identify the candidate itself.
   * False when the candidate's name is already the group header above it —
   * the job title takes the first line instead.
   */
  showCandidateName?: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', payload: GlobalSearchApplicationRow): void;
}>();

const jobTitle = computed(
  () => props.row.application.jobDisplayTitle || `Job #${props.row.application.jobId}`,
);

const stageLabel = computed(() => getStageLabel(props.row.application.applicationStatus));

const jobStateTag = computed(() => getJobStateTag(props.row.application.jobStatus));

/**
 * Application-level activity only (Applied/Updated). Candidate-level message
 * activity is deliberately not used here, because a per-application row must
 * never imply job-specific message activity the data cannot support. The
 * verb is kept ("Applied" vs "Updated" is real information) with a compact
 * duration so the fixed right rail never squeezes the job title.
 */
const activityLabel = computed(() => {
  const { applicationApplyTs, updateTs } = props.row.application;
  if (applicationApplyTs) return `Applied ${formatCompactTimeAgo(applicationApplyTs)}`;
  if (updateTs) return `Updated ${formatCompactTimeAgo(updateTs)}`;
  return '';
});

const nameParts = computed(() => {
  if (isPhoneLikeQuery(props.query)) {
    return [{ text: props.row.conversationIndex.proName, isMatch: false }];
  }
  return splitTextForHighlight(props.row.conversationIndex.proName, props.query);
});
</script>

<!-- Bold-primary always means candidate name; every stage pill stays neutral
     gray so the query highlight is the list's only color. -->
<template>
  <button
    type="button"
    class="block w-full rounded-lg text-left transition-colors hover:bg-tint-40"
    :class="showCandidateName ? 'px-4 py-2' : 'py-1.5 pl-2 pr-4'"
    data-test="global-search-row"
    :data-test-application-id="row.application.applicationId"
    @click="emit('select', row)"
  >
    <div class="flex items-baseline justify-between gap-2">
      <span
        v-if="showCandidateName"
        class="sensitive min-w-0 truncate text-sm font-semibold text-shade-880"
        data-test="global-search-row-name"
      >
        <template v-for="(part, index) in nameParts" :key="index">
          <mark v-if="part.isMatch" class="rounded-[3px] bg-highlight-100 text-shade-880">{{
            part.text
          }}</mark>
          <template v-else>{{ part.text }}</template>
        </template>
      </span>
      <span
        v-else
        class="min-w-[56px] truncate text-xs font-medium text-shade-880"
        :title="jobTitle"
        data-test="global-search-row-job"
      >
        {{ jobTitle }}
      </span>
      <span
        v-if="activityLabel"
        class="shrink-0 whitespace-nowrap text-xs tabular-nums text-shade-700"
        data-test="global-search-row-activity"
      >
        {{ activityLabel }}
      </span>
    </div>

    <div class="mt-1 flex min-w-0 items-center gap-1.5">
      <span
        v-if="showCandidateName"
        class="min-w-[56px] truncate text-xs text-shade-700"
        :title="jobTitle"
        data-test="global-search-row-job"
        >{{ jobTitle }}</span
      >
      <span
        class="shrink-0 rounded bg-tint-40 px-[7px] py-[3px] text-[11px] font-medium leading-none text-shade-820"
        data-test="global-search-row-stage"
        >{{ stageLabel }}</span
      >
      <span
        v-if="jobStateTag"
        class="shrink-0 rounded border border-tint-80 px-[7px] py-[2px] text-[11px] font-medium leading-none text-shade-820"
        data-test="global-search-row-job-state"
      >
        {{ jobStateTag.label }}
      </span>
    </div>
  </button>
</template>
